import React, { useCallback, useEffect, useRef, useState } from "react";
import { Toggle } from "@velaro/velaro-component-lib";
import { Shift, WorkSchedule } from "./types";
import { findConflictingIndices, findTeamScheduleConflict } from "./scheduleUtils";
import useTeams from "@/hooks/useTeams";
import useApi from "@/hooks/useApi";
import ShiftForm from "./ShiftForm";
import daysOfWeek from "./helpers/daysOfWeek";

interface AgentOption {
  id: number;
  firstName: string;
  lastName: string;
}

interface Props {
  schedule: WorkSchedule;
  allSchedules: WorkSchedule[];
  onChange: (updated: WorkSchedule) => void;
}

export default function ScheduleEditor({ schedule, allSchedules, onChange }: Props) {
  const { teams } = useTeams();
  const [agents, setAgents] = useState<AgentOption[]>([]);
  const [editingName, setEditingName] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const api = useApi();

  // Reset name edit state when switching to a different schedule
  useEffect(() => {
    setEditingName(false);
  }, [schedule.id]);

  useEffect(() => {
    api.messaging.get("Users/List").then(async (resp) => {
      if (resp.ok) {
        const data = await resp.json();
        setAgents(
          Array.isArray(data)
            ? data.map((u: any) => ({ id: u.id, firstName: u.firstName ?? "", lastName: u.lastName ?? "" }))
            : []
        );
      }
    }).catch(() => {});
  }, [api.messaging]);

  useEffect(() => {
    if (editingName && nameRef.current) {
      nameRef.current.focus();
      nameRef.current.select();
    }
  }, [editingName]);

  const update = useCallback(
    (patch: Partial<WorkSchedule>) => onChange({ ...schedule, ...patch }),
    [schedule, onChange]
  );

  function commitName() {
    const value = nameRef.current?.value.trim() || "Untitled Schedule";
    update({ name: value });
    setEditingName(false);
  }

  function onAddShift() {
    const shifts = schedule.shifts ?? [];
    let next: Shift;
    if (shifts.length > 0) {
      const last = shifts[shifts.length - 1];
      next = {
        ...last,
        startDayOfWeek: Math.min(last.startDayOfWeek + 1, 6),
        endDayOfWeek: Math.min(last.endDayOfWeek + 1, 6)
      };
    } else {
      next = {
        startDayOfWeek: daysOfWeek.Monday,
        startHour: 9, startMinute: 0,
        endDayOfWeek: daysOfWeek.Monday,
        endHour: 17, endMinute: 0
      };
    }
    update({ shifts: [...shifts, next] });
  }

  const conflictingIndices = findConflictingIndices(schedule.shifts ?? []);

  return (
    <div className="p-6 space-y-6 max-w-3xl">

      {/* ── Header: name + enabled toggle ── */}
      <div className="flex items-center gap-3">
        {editingName ? (
          <input
            key={schedule.id}
            ref={nameRef}
            type="text"
            defaultValue={schedule.name}
            onBlur={commitName}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitName();
              if (e.key === "Escape") setEditingName(false);
            }}
            className="text-xl font-semibold text-slate-800 bg-transparent border-b-2 border-cornflower-blue-400 outline-none flex-1 min-w-0 py-0.5"
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditingName(true)}
            className="text-xl font-semibold text-slate-800 hover:text-cornflower-blue-700 text-left flex items-center gap-2 group transition-colors min-w-0"
          >
            <span className="truncate">{schedule.name}</span>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" className="shrink-0 opacity-0 group-hover:opacity-50 transition-opacity">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
        <div className="ml-auto flex items-center gap-2 shrink-0">
          <span className="text-sm text-slate-500">Enabled</span>
          <Toggle
            toggled={schedule.enabled}
            changeToggle={() => update({ enabled: !schedule.enabled })}
          />
        </div>
      </div>

      {/* ── Shifts ── */}
      <Section
        title="Shift Hours"
        action={
          <button
            type="button"
            onClick={onAddShift}
            className="flex items-center gap-1 text-xs text-cornflower-blue-600 hover:text-cornflower-blue-800 font-medium transition-colors"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Add shift
          </button>
        }
      >
        {(schedule.shifts ?? []).length === 0 ? (
          <p className="text-sm text-slate-400 py-1">
            No shifts yet — click &ldquo;Add shift&rdquo; above.
          </p>
        ) : (
          <div className="space-y-2">
            {schedule.shifts.map((shift, i) => {
              const conflict = conflictingIndices.has(i);
              return (
                <div
                  key={i}
                  className={`rounded-lg transition-colors ${
                    conflict ? "ring-1 ring-red-300 bg-red-50" : ""
                  }`}
                >
                  <ShiftForm
                    shift={shift}
                    onChange={(s) =>
                      update({ shifts: schedule.shifts.map((x, idx) => (idx === i ? s : x)) })
                    }
                    onDelete={() =>
                      update({ shifts: schedule.shifts.filter((_, idx) => idx !== i) })
                    }
                  />
                  {conflict && (
                    <div className="px-3 pb-2 -mt-1 text-xs text-red-600 flex items-center gap-1.5">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                          stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                        />
                      </svg>
                      Overlaps with another shift in this schedule
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* ── Teams ── */}
      <Section
        title="Assigned Teams"
        badge={schedule.teamIds?.length}
        description="A team can only belong to one schedule at a time."
      >
        {teams.length === 0 ? (
          <p className="text-sm text-slate-400 py-1">No teams configured.</p>
        ) : (
          <div className="space-y-0.5 max-h-52 overflow-y-auto -mx-1">
            {teams.map((team) => {
              const checked = (schedule.teamIds ?? []).includes(team.id);
              const conflict = !checked
                ? findTeamScheduleConflict(allSchedules, team.id, schedule.id)
                : undefined;
              return (
                <label
                  key={team.id}
                  className={`flex items-center gap-2.5 px-3 py-1.5 rounded-lg transition-colors ${
                    conflict ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-slate-50"
                  }`}
                  title={conflict ? `Already assigned to "${conflict.name}"` : undefined}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={!!conflict}
                    onChange={() => {
                      if (conflict) return;
                      const current = schedule.teamIds ?? [];
                      update({
                        teamIds: checked
                          ? current.filter((id) => id !== team.id)
                          : [...current, team.id]
                      });
                    }}
                    className="rounded"
                  />
                  <span className="text-sm text-slate-700">{team.name}</span>
                  {conflict && (
                    <span className="text-xs text-amber-600 ml-auto shrink-0">
                      In &ldquo;{conflict.name}&rdquo;
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        )}
      </Section>

      {/* ── Agents ── */}
      <Section
        title="Assigned Agents"
        badge={schedule.userIds?.length}
        description="These agents will be marked unavailable outside their scheduled shift hours."
      >
        {agents.length === 0 ? (
          <p className="text-sm text-slate-400 py-1">No agents available.</p>
        ) : (
          <div className="space-y-0.5 max-h-52 overflow-y-auto -mx-1">
            {agents.map((agent) => {
              const checked = (schedule.userIds ?? []).includes(agent.id);
              return (
                <label
                  key={agent.id}
                  className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      const current = schedule.userIds ?? [];
                      update({
                        userIds: checked
                          ? current.filter((id) => id !== agent.id)
                          : [...current, agent.id]
                      });
                    }}
                    className="rounded"
                  />
                  <span className="text-sm text-slate-700">
                    {agent.firstName} {agent.lastName}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </Section>
    </div>
  );
}

// ─── Section wrapper ─────────────────────────────────────────────────────────

function Section({
  title,
  badge,
  description,
  action,
  children
}: {
  title: string;
  badge?: number;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
          {badge !== undefined && badge > 0 && (
            <span className="text-xs bg-cornflower-blue-100 text-cornflower-blue-700 px-1.5 py-0.5 rounded-full font-medium">
              {badge}
            </span>
          )}
        </div>
        {action}
      </div>
      {description && (
        <p className="text-xs text-slate-400 px-4 pt-3 -mb-1">{description}</p>
      )}
      <div className="p-4">{children}</div>
    </div>
  );
}
